import { ChevronRight, Eye, ExternalLink, PanelRightClose, Pin, Sparkles } from "lucide-react";
import { type ComponentType, useEffect, useState } from "react";

import { AppErrorBoundary } from "@/components/feedback";
import { InspectorPanel } from "@/components/rail/InspectorPanel";
import { ProjectsPanel } from "@/components/projects/ProjectsPanel";
import { Markdown } from "@/components/shared/Markdown";
import { Button } from "@/components/ui/button";
import { Tip } from "@/components/ui/overlays";
import { ScrollArea, Badge } from "@/components/ui/primitives";
import { EmptyState, ErrorState, LoadingState } from "@/components/feedback";
import { workspace } from "@/components/workspace/controller";
import { useWorkspaceStore } from "@/components/workspace/store";
import { useAnnotations } from "@/hooks/queries";
import { GraphMindApiError, type NodeOut } from "@/lib/api";
import { cn, timeAgo } from "@/lib/utils";
import { useGraph } from "@/stores/graph";
import { TopBarToggle } from "@/components/appshell/TopBarToggle";
import { useShell, type RightBarPane } from "@/stores/shell";

function PaneTab({
  icon: Icon,
  label,
  active,
  onClick,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-1.5 rounded px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest transition-colors",
        active ? "text-primary" : "text-muted-foreground hover:text-foreground",
      )}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

/**
 * A saved AI explanation / note. Expands IN PLACE — opening it must never
 * unmount whatever document/panel is currently active (the reader, if one is
 * open, stays mounted and its text stays selectable). "Open in graph" is an
 * explicit, secondary action for when the user actually wants the atlas —
 * the ONLY thing that navigates. This mirrors the reader's own AnnotationRow
 * before it was retired (annotations now live exclusively here).
 */
function AnnotationRow({ node }: { node: NodeOut }) {
  const isAi = node.type === "ai_output";
  const [open, setOpen] = useState(false);
  return (
    <li className="rounded-md border border-border bg-muted/40 transition-colors hover:border-primary/40">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full p-2.5 text-left"
      >
        <div className="flex items-center gap-2">
          <Badge className={isAi ? "border-primary/40 text-primary" : "border-border text-muted-foreground"}>
            {isAi ? "AI" : "note"}
          </Badge>
          <ChevronRight className={cn("h-3 w-3 text-muted-foreground transition-transform", open && "rotate-90")} />
          <span className="ml-auto font-mono text-[10px] text-muted-foreground">{timeAgo(node.created_at)}</span>
        </div>
        {!open && (
          <p className="mt-1.5 line-clamp-3 text-[12.5px] leading-relaxed text-foreground/80">
            {node.summary || node.label}
          </p>
        )}
      </button>
      {open && (
        <div className="border-t border-border px-2.5 py-2">
          {node.summary ? <Markdown text={node.summary} /> : <p className="text-[12.5px] italic text-muted-foreground">{node.label}</p>}
          <button
            type="button"
            onClick={() => {
              workspace.openGraph();
              useGraph.getState().requestFocus(node.id);
            }}
            className="mt-2 flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground hover:text-primary"
          >
            <ExternalLink className="h-3 w-3" /> Open in graph
          </button>
        </div>
      )}
    </li>
  );
}

/** Only mounted once a real document id is known — `useAnnotations` has no
 * `enabled` guard of its own (unlike `useMarkup`), so gating happens by
 * conditionally mounting this component rather than conditionally calling
 * the hook. */
function AnnotationsList({ docId }: { docId: string }) {
  const annotations = useAnnotations(docId);
  if (annotations.isLoading) return <LoadingState variant="skeleton" rows={4} className="p-2.5" />;
  // GET /api/documents/{id}/annotations returns [] for a document that simply
  // has no annotations yet — it does NOT 404. A 404 means the document is
  // unknown or not this user's, i.e. a real error. This branch nevertheless
  // swallows the 404 into the same EmptyState as an empty list, so a stale or
  // foreign doc id reads as "nothing here" instead of surfacing. Any other
  // status (500, network, etc.) keeps ErrorState.
  const notFound = annotations.error instanceof GraphMindApiError && annotations.error.status === 404;
  if (annotations.isError && !notFound) {
    return (
      <ErrorState
        error={annotations.error}
        onRetry={() => void annotations.refetch()}
        label="annotations"
        className="p-4"
      />
    );
  }
  const list = notFound ? [] : (annotations.data ?? []);
  if (list.length === 0) {
    return (
      <EmptyState
        icon={Sparkles}
        title="No annotations yet"
        description="Highlight text in the reader and explain it — saved notes land here."
      />
    );
  }
  return (
    <ScrollArea className="h-full">
      <ul className="space-y-1.5 p-2.5">
        {list.map((n) => (
          <AnnotationRow key={n.id} node={n} />
        ))}
      </ul>
    </ScrollArea>
  );
}

function AnnotationsPane() {
  // Derived straight from the workspace's active panel (not the `stores/tabs`
  // shim) — a 'doc' panel is a reader document; a 'markdown' panel's own id
  // (there is no separate node lookup) is its docId.
  const docId = useWorkspaceStore((s) => {
    const p = s.panels[s.activeId ?? ""];
    if (!p) return undefined;
    if (p.type === "doc") return p.data?.docId as string | undefined;
    if (p.type === "markdown") return p.data?.docId as string | undefined;
    return undefined;
  });

  if (!docId) {
    return (
      <EmptyState
        icon={Sparkles}
        title="No document open"
        description="Open a document to see its AI-saved explanations and notes here."
      />
    );
  }
  return <AnnotationsList docId={docId} />;
}

/**
 * The right rail — collapsed by default, a small edge button expands it.
 * Toggles between Inspect (the per-node/edge InspectorPanel), AI annotations
 * (saved explanations/notes on the active document), and Pins (the existing
 * `ProjectsPanel`, embedded).
 */
/** The context panel's panes, in one list.
 *
 * Both the collapsed rail and the expanded header render from this, so what the
 * rail advertises is always what the panel actually holds. */
const RAIL_PANES: { id: RightBarPane; label: string; icon: typeof Eye }[] = [
  { id: "inspect", label: "Details", icon: Eye },
  { id: "annotations", label: "Saved", icon: Sparkles },
  { id: "pins", label: "Pins", icon: Pin },
];

export function RightBar() {
  const open = useShell((s) => s.rightOpen);
  const pane = useShell((s) => s.rightBarPane);
  const selection = useGraph((s) => s.selection);

  // A canvas selection pops the inspector open — mirrors the old
  // rail/RightRail.tsx behavior ("Selecting a node/edge in the atlas pops the
  // inspector open"). Keyed only on `selection` (not `open`/`pane`) so this
  // never fights a user who manually closes the bar or switches panes while
  // the SAME selection is still live — it only fires on a NEW selection.
  useEffect(() => {
    if (selection) useShell.getState().setRightBarPane("inspect");
  }, [selection]);

  if (!open) {
    return (
      // Both toggles sit at the TOP of the spine, mirroring the left rail: the
      // controls that open chrome belong at the edge you reach for, not parked
      // at the bottom of a full-height column.
      //
      // w-11 MATCHES the LeftBar's collapsed spine exactly. It used to be w-8,
      // which is 12px narrower — and since both rails flank the workspace, that
      // difference pushed every centered surface in the app (Home's feed, any
      // centered panel content) 6px right of the window's true centre. It also
      // left these 32px buttons flush against both edges while the left rail's
      // identical buttons had 6px of breathing room either side.
      <nav
        aria-label="Context panel"
        className="flex w-11 shrink-0 flex-col items-center gap-1 border-l border-border py-2"
      >
        {RAIL_PANES.map(({ id, label, icon: Icon }) => (
          <Tip key={id} label={`${label} — ]`} side="left">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label={label}
              aria-expanded={false}
              aria-controls="gv-context"
              // setRightBarPane already opens the bar; toggling as well closed
              // it straight back again.
              onClick={() => useShell.getState().setRightBarPane(id)}
            >
              <Icon className="h-4 w-4" />
            </Button>
          </Tip>
        ))}
        <span className="my-0.5 h-px w-5 bg-border" aria-hidden="true" />
        <TopBarToggle side="left" />
      </nav>
    );
  }

  const setPane = (p: RightBarPane) => useShell.getState().setRightBarPane(p);

  return (
    <aside
      id="gv-context"
      aria-label="Context"
      className="flex w-[360px] shrink-0 flex-col border-l border-border bg-sidebar text-sidebar-foreground"
    >
      <div className="flex h-12 shrink-0 items-center gap-1 border-b border-sidebar-border/60 px-2.5">
        {RAIL_PANES.map(({ id, label, icon }) => (
          <PaneTab
            key={id}
            icon={icon}
            label={label}
            active={pane === id}
            onClick={() => setPane(id)}
          />
        ))}
      </div>
      {/* The panel gets its OWN boundary. It renders whatever the current
          selection is, and a single unexpected field in one node's payload
          used to throw past here and blank the entire studio — the shell
          boundary is the last one before a white screen, and a side panel is
          not worth the whole workspace. Selecting something else resets it,
          which is also the natural way out of a bad node. */}
      <AppErrorBoundary label="context panel" resetKey={`${pane}:${selection?.kind ?? ""}:${selection?.id ?? ""}`}>
        <div className="min-h-0 flex-1 overflow-hidden">
          {pane === "pins" ? (
            <ProjectsPanel embedded onClose={() => useShell.getState().toggleRightBar()} />
          ) : pane === "inspect" ? (
            <ScrollArea className="h-full">
              <InspectorPanel />
            </ScrollArea>
          ) : (
            <AnnotationsPane />
          )}
        </div>
      </AppErrorBoundary>
      {/* Collapse control pinned to the BOTTOM-RIGHT — a mirror of the LeftBar's
          collapse idiom. Note the collapsed spine's open-button is NOT in this
          corner: it sits at the TOP of the spine, see above. */}
      <div className="flex shrink-0 items-center justify-end border-t border-sidebar-border/60 p-1.5">
        <Tip label="Collapse right bar — ]" side="left">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => useShell.getState().toggleRightBar()}
            aria-label="Collapse right bar"
            aria-expanded
            aria-controls="gv-context"
          >
            <PanelRightClose className="h-4 w-4" />
          </Button>
        </Tip>
      </div>
    </aside>
  );
}
